import React, { useState, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { Brain, Settings, Zap, Info } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";
const API = `${BACKEND_URL}/api`;

const AINode = ({ data, selected }) => {
  const [showConfig, setShowConfig] = useState(false);
  const [availableModels, setAvailableModels] = useState({});
  const [config, setConfig] = useState({
    model: 'gpt-4o',
    prompt: '',
    systemMessage: 'You are a helpful AI assistant for workflow automation.',
    outputVariables: [],
    temperature: 0.7,
    maxTokens: 1000,
    ...data.config
  });

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await fetch(`${API}/workflows/ai/models`);
        if (response.ok) {
          const models = await response.json();
          setAvailableModels(models);
        }
      } catch (error) {
        console.error('Error fetching AI models:', error);
      }
    };

    if (showConfig) {
      fetchModels();
    }
  }, [showConfig]);

  const handleSave = () => {
    // Update node data with new configuration
    if (data.onConfigUpdate) {
      data.onConfigUpdate(config);
    }
    setShowConfig(false);
  };
  
  return (
    <>
      <div className={`px-4 py-2 shadow-md rounded-lg bg-purple-500 text-white border-2 ${
        selected ? 'border-purple-300' : 'border-purple-600'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Brain className="h-4 w-4" />
            <div className="text-sm font-medium">{data.label}</div>
          </div>
          <button
            onClick={() => setShowConfig(true)}
            className="p-1 hover:bg-purple-600 rounded"
          >
            <Settings className="h-3 w-3" />
          </button>
        </div>
        
        {config.model && (
          <div className="text-xs mt-1 opacity-90">
            {config.model}
          </div>
        )}
        
        <Handle
          type="target"
          position={Position.Top}
          style={{
            background: '#a855f7',
            border: '2px solid #9333ea',
          }}
        />
        <Handle
          type="source"
          position={Position.Bottom}
          style={{
            background: '#a855f7',
            border: '2px solid #9333ea',
          }}
        />
      </div>

      {showConfig && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[600px] max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Brain className="h-5 w-5 mr-2" />
              Configure AI Node
            </h3>
            
            <div className="space-y-4">
              {/* AI Model Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">AI Model</label>
                <select 
                  value={config.model}
                  onChange={(e) => setConfig({...config, model: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  {Object.entries(availableModels).map(([name, details]) => (
                    <option key={details.id} value={details.id}>
                      {name} ({details.provider})
                    </option>
                  ))}
                </select>
                {config.model && availableModels[Object.keys(availableModels).find(k => availableModels[k].id === config.model)] && (
                  <div className="mt-2 p-2 bg-blue-50 rounded-lg">
                    <div className="text-xs text-blue-800">
                      <strong>Recommended for:</strong> {availableModels[Object.keys(availableModels).find(k => availableModels[k].id === config.model)]?.recommended_for?.join(', ')}
                    </div>
                  </div>
                )}
              </div>

              {/* System Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">System Message</label>
                <textarea 
                  value={config.systemMessage}
                  onChange={(e) => setConfig({...config, systemMessage: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Instructions for the AI model..."
                  rows="3"
                />
              </div>

              {/* Prompt */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prompt
                  <Info className="inline h-4 w-4 ml-1 text-gray-400" title="Use {{variable}} to reference workflow variables" />
                </label>
                <textarea 
                  value={config.prompt}
                  onChange={(e) => setConfig({...config, prompt: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Your prompt here. Use {{variable}} to reference workflow variables..."
                  rows="4"
                />
                <div className="mt-1 text-xs text-gray-500">
                  Use {`{{variable}}`} syntax to reference variables from previous nodes
                </div>
              </div>

              {/* Advanced Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Temperature</label>
                  <input 
                    type="number"
                    min="0"
                    max="1"
                    step="0.1"
                    value={config.temperature}
                    onChange={(e) => setConfig({...config, temperature: parseFloat(e.target.value)})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                  <div className="text-xs text-gray-500 mt-1">0 = deterministic, 1 = creative</div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Max Tokens</label>
                  <input 
                    type="number"
                    min="1"
                    max="4000"
                    value={config.maxTokens}
                    onChange={(e) => setConfig({...config, maxTokens: parseInt(e.target.value)})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
              </div>

              {/* Output Variables */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Output Variables</label>
                <input 
                  value={config.outputVariables?.join(', ') || ''}
                  onChange={(e) => setConfig({...config, outputVariables: e.target.value.split(',').map(s => s.trim()).filter(s => s)})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="result, extracted_data, summary"
                />
                <div className="text-xs text-gray-500 mt-1">
                  Comma-separated variable names to extract from AI response
                </div>
              </div>

              {/* Test Button */}
              <div className="bg-gray-50 rounded-lg p-4">
                <button
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  onClick={async () => {
                    try {
                      const response = await fetch(`${API}/workflows/ai/test`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          model: config.model,
                          prompt: config.prompt || 'Hello, this is a test message.'
                        })
                      });
                      const result = await response.json();
                      alert(`Test Result: ${result.test_result?.output || 'Test completed'}`);
                    } catch (error) {
                      alert(`Test Failed: ${error.message}`);
                    }
                  }}
                >
                  <Zap className="h-4 w-4" />
                  <span>Test AI Connection</span>
                </button>
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => setShowConfig(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Save Configuration
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AINode;